import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';
import RequestForecastChangeModal from 'src/components/modals/RequestForecastChangeModal/RequestForecastChangeModal';
import { currentLocale, localeNumber } from 'src/localization/locale';
import { AppliedPromotion, CustomerForecast, CustomerForecastLine } from 'src/services/forecasts';
import { isInPlanningWindow } from './planningWindow';

interface Props {
  forecast: CustomerForecast;
  onClose: () => void;
}

interface DayRow {
  date: Date;
  weekdayIdx: number; // 0=Mon..6=Sun
  forecast: number | null;
  baseForecast: number | null;
  appliedPromotions: AppliedPromotion[];
  actual: number | null;
  possible: number | null;
  delta: number | null;
}

function buildRows(f: CustomerForecast): DayRow[] {
  const days = f.days_in_month;
  const byDay = new Map<number, CustomerForecastLine>();
  for (const l of f.lines) byDay.set(l.day, l);
  const rows: DayRow[] = [];
  for (let d = 1; d <= days; d++) {
    const date = new Date(f.year, f.month - 1, d);
    const js = date.getDay();
    const idx = (js + 6) % 7;
    const isWeekend = idx >= 5;
    const line = byDay.get(d);
    const base = line?.pakete ?? null;
    const boosted = line?.pakete_boosted ?? null;
    rows.push({
      date,
      weekdayIdx: idx,
      forecast: isWeekend ? null : boosted ?? base ?? 0,
      baseForecast: isWeekend ? null : base ?? 0,
      appliedPromotions: line?.applied_promotions ?? [],
      actual: line?.actual_quantity ?? null,
      possible: line?.possible ?? null,
      delta: line?.delta_quantity ?? null,
    });
  }
  return rows;
}

function PromoBadge({ promos }: { promos: AppliedPromotion[] }) {
  if (promos.length === 0) return null;
  const maxMult = promos.filter(p => p.kind === 'multiplier').reduce((m, p) => Math.max(m, p.value), 0);
  const totalAdd = promos.filter(p => p.kind === 'additional').reduce((s, p) => s + p.value, 0);
  const parts: string[] = [];
  if (maxMult > 0) parts.push(`${maxMult.toFixed(2)}×`);
  if (totalAdd > 0) parts.push(`+${Math.round(totalAdd)}`);
  return (
    <Badge type={BadgeType.Warning} label={parts.join(' ')} expanded hoverText={promos.map(p => p.name).join(', ')} />
  );
}

export default function ForecastSubmittedView({ forecast, onClose }: Props) {
  const { t } = useTranslation();
  const rows = buildRows(forecast);
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  // Only months still inside the planning window may be changed. The running
  // month, the gap month and anything already past (the "Verlauf") are locked.
  const canRequestChange = forecast.state === 'approved' && isInPlanningWindow(forecast, new Date());

  const stateLabel = t(`forecasts.state.${forecast.state}`);
  const monthLabel = new Date(forecast.year, forecast.month - 1, 1).toLocaleDateString(currentLocale(), {
    year: 'numeric',
    month: 'long',
  });

  return (
    <section className="surface-elevated border border-subtle rounded-base card-shadow overflow-hidden">
      <div className="px-24 py-16 border-subtle-bottom flex items-center justify-between">
        <div className="flex items-center gap-8">
          <h2 className="typo-text-16--bold text-full">{monthLabel}</h2>
          <Badge
            className="max-w-min"
            label={stateLabel}
            expanded
            type={
              forecast.state === 'approved'
                ? BadgeType.Successful
                : forecast.state === 'rejected'
                  ? BadgeType.Error
                  : BadgeType.Neutral2
            }
          />
        </div>
        <button type="button" onClick={onClose} className="typo-text-14 text-faded hover:text-full">
          {t('forecasts.close')}
        </button>
      </div>

      <div className="px-24 py-16 flex flex-col gap-16">
        {forecast.state === 'rejected' && (
          <div className="status-error-bg rounded-base px-16 py-12 typo-text-14 status-error-full">
            {t('forecasts.rejected_banner', {
              reason: forecast.rejection_reason || t('forecasts.no_reason_given'),
            })}
          </div>
        )}

        {forecast.state === 'approved' && (
          <div className="flex items-center gap-8 text-faded">
            <Icon name="lock" width={16} height={16} />
            <span className="typo-text-12">{t('forecasts.locked_hint')}</span>
          </div>
        )}

        <div className="flex items-end justify-between gap-16">
          <div className="flex gap-32">
            <div>
              <div className="typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecasts.col.forecast')}
              </div>
              <div className="typo-text-16--bold text-full">
                {localeNumber(forecast.total_pakete_boosted ?? forecast.total_pakete)}
              </div>
              {(forecast.total_pakete_boosted ?? forecast.total_pakete) > forecast.total_pakete && (
                <div className="typo-text-12 text-faded">
                  {t('forecasts.plannedOriginal', { value: localeNumber(forecast.total_pakete) })}
                </div>
              )}
            </div>
            {forecast.deadline && (
              <div>
                <div className="typo-text-10 text-faded font-semibold uppercase tracking-wider">
                  {t('forecasts.col.deadline')}
                </div>
                <div className="typo-text-16--bold text-full">
                  {new Date(forecast.deadline).toLocaleDateString(currentLocale(), {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </div>
              </div>
            )}
          </div>
          {canRequestChange && (
            <Button
              tone={ButtonToneProps.SECONDARY}
              title={t('forecasts.requestChange')}
              onClick={() => setChangeModalOpen(true)}
            />
          )}
        </div>
      </div>
      <RequestForecastChangeModal
        forecast={forecast}
        isOpen={changeModalOpen}
        onClose={() => setChangeModalOpen(false)}
      />

      <div className="border-t border-subtle">
        <table className="w-full">
          <thead className="surface-base">
            <tr className="border-b border-subtle">
              <th className="px-24 py-12 text-left typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecasts.col.day')}
              </th>
              <th className="px-24 py-12 text-right typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecasts.col.forecast')}
              </th>
              <th className="px-24 py-12 text-center typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecasts.col.actual')}
              </th>
              <th className="px-24 py-12 text-center typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecasts.col.possible')}
              </th>
              <th className="px-24 py-12 text-center typo-text-10 text-faded font-semibold uppercase tracking-wider">
                {t('forecasts.col.delta')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const isWeekend = r.weekdayIdx >= 5;
              return (
                <tr
                  key={r.date.toISOString()}
                  className={`border-b border-subtle ${isWeekend ? 'surface-base text-faded' : ''}`}>
                  <td className="px-24 py-8 text-left typo-text-12">
                    {r.date.toLocaleDateString(currentLocale(), { weekday: 'short' })},{' '}
                    {r.date.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit' })}
                  </td>
                  <td className="px-24 py-8 typo-text-14--bold text-full">
                    <div className="flex items-center justify-end gap-8">
                      <PromoBadge promos={r.appliedPromotions} />
                      <span>{r.forecast === null ? '—' : r.forecast}</span>
                    </div>
                  </td>
                  <td className="px-24 py-8 text-center typo-text-14 text-faded">
                    {r.actual === null ? '—' : r.actual}
                  </td>
                  <td className="px-24 py-8 text-center typo-text-14 text-faded">
                    {r.possible === null ? '—' : r.possible}
                  </td>
                  <td className="px-24 py-8 text-center typo-text-14">
                    {r.delta === null ? (
                      <span className="text-faded">—</span>
                    ) : (
                      <span className={r.delta < 0 ? 'status-error-full' : 'text-faded'}>
                        {r.delta > 0 ? `+${r.delta}` : r.delta}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
